import { Slider, Typography, Stack, SliderProps } from '@mui/material';
import { useReaderConfiguration } from './ReaderConfigurationContext';
import { NumericConfiguration } from './utils/ConfigUtils';

interface SliderSelectionProps {
  id: string;
  configKey: keyof NumericConfiguration;
  range: { min: number; max: number; step: number };
  inputLabel: string;
  unitLabel?: string;
  marks?: SliderProps['marks'];
  valueLabelFormat?: SliderProps['valueLabelFormat'];
}

export default function ConfigSlider({
  configKey,
  id,
  inputLabel,
  range,
  unitLabel,
  marks,
  valueLabelFormat,
}: Readonly<SliderSelectionProps>) {
  const { config, updateConfig } = useReaderConfiguration();
  const handleChange = (_: Event, newValue: number | number[]) => {
    updateConfig({ [configKey]: newValue as number });
  };

  const value = config[configKey];

  return (
    <Stack direction='column'>
      <Stack
        id={`${id}`}
        component='label'
        data-testid={id}
        direction='row'
        justifyContent='space-between'
        gap={1}
      >
        <Typography variant='body2' component='span'>
          {inputLabel}
        </Typography>
        {!valueLabelFormat && (
          <Typography variant='body2' component='span' color='text.secondary'>
            {config[configKey]}
            {unitLabel}
          </Typography>
        )}
        {typeof valueLabelFormat === 'function' && (
          <Typography variant='body2' component='span' color='text.secondary'>
            {valueLabelFormat(value, 0)}
            {unitLabel}
          </Typography>
        )}
        {typeof valueLabelFormat === 'string' && (
          <Typography variant='body2' component='span' color='text.secondary'>
            {`${valueLabelFormat}${unitLabel}`}
          </Typography>
        )}
      </Stack>
      <Stack direction='row' alignItems='center' gap={2}>
        <Slider
          size='small'
          aria-labelledby={`${id}`}
          aria-valuetext={`${value}`}
          getAriaValueText={() => `${value}`}
          value={config[configKey]}
          onChange={handleChange}
          valueLabelFormat={valueLabelFormat}
          min={range.min}
          max={range.max}
          step={range.step}
          marks={marks}
        />
      </Stack>
    </Stack>
  );
}
